import React, { useState } from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import Image from 'next/image'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'
import CryptoCardGrid from '../src/components/Product/rewards/CryptoCards/Grid'
import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'

import { CryptoCard as ICryptoCard } from '../types/cryptoCard'
import { getCryptoCards } from '../src/services/cards'
import { Region } from '../types/region'
import { Filters } from '../types/general'
import { getRegions } from '../src/services/regions'

import FiltersProvider from '../src/contexts/Filters'

import CryptoCardFilters from '../src/components/Filters/CryptoCard'
import LearnMore from '../src/components/Product/LearnMore'

const SeoText = dynamic(() => import('../src/components/Product/rewards/CryptoCards/SeoText'))

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
}))

const faqs = [
  {
    question: 'How do crypto cards work?',
    answer:
      'A crypto debit card is exactly like a regular payment card, except that you have the option to load it with crypto instead of fiat. In most cases the provider offers an app that holds both crypto and fiat currencies like Euros or US Dollars. When users want to make a purchase in a store (or online ) they can convert their funds into fiat within the app and spend those funds instantly with their crypto debit card.',
  },
  {
    question: 'How do crypto cards make money?',
    answer:
      'Most crypto card providers make money when you are selling crypto. They also receive a small commission from Visa/Mastercard when a user makes a purchase in-store. Some offer it only to get better customer retention.',
  },
  {
    question: 'Does a crypto card make sense?',
    answer:
      'A crypto card makes sense if you need quick access to funds but you have a lot of your wealth in crypto. With a crypto card you can instantly make a purchase using your crypto whereas if you would withdraw the funds to your bank account it would take 2-3 days until you can spend it.',
  },
  {
    question: 'Are the crypto rewards worth it?',
    answer:
      'Crypto rewards are attractive if you use it as your primary card and start earning rewards on all your spendings. It’s important that they offer fiat top-ups by card so that you can transfer funds from your main bank account and spend your fiat instead of spending crypto. If you have to sell crypto the money you lose on the conversion might cancel out the crypto rewards. ',
  },
]

interface props {
  cards: ICryptoCard[]
  regions: Region[]
}

const Cards = ({ cards, regions }: props): JSX.Element => {
  const classes = useStyles({})
  const filteredInitialCryptoCards = cards.filter((obj) =>
    obj.regions.find((obj) => obj.name === 'Europe'),
  )

  const [selectedCryptoCards, setSelectedCryptoCards] = useState<ICryptoCard[]>(
    filteredInitialCryptoCards,
  )

  function onFilterCards(filters: Filters) {
    const filteredRegion = filters.region
    if (filteredRegion) {
      const newCards = cards.filter((obj) =>
        obj.regions.find((obj) => obj.name === filteredRegion.name),
      )
      setSelectedCryptoCards(newCards)
    }
  }

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <FiltersProvider>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`The Best Crypto Cards`}
            description={`Spend your cryptocurrency in stores or online using a conventional payment card. You won’t need a bank account anymore.`}
            openGraph={{
              url: `https://cryptotesters.com${urls.cryptoCards}`,
              title: `The Best Crypto Cards`,
              description: `Spend your cryptocurrency in stores or online using a conventional payment card. You won’t need a bank account anymore.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: 'https://cryptotesters.com/sharable_small.png',
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">
                    The best <span className={classes.purpleText}> crypto cards </span>
                  </Typography>
                  <Typography variant="body2">
                    Take advantage of our exclusive deals with some of the best companies in the
                    crypto space.
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    href="#cards"
                    className={classes.heroCTA}
                  >
                    Learn more about Cards
                  </Button>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <Image src="/rewards/cards.png" objectFit="contain" layout="fill" />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>

            <div className={classes.section} id="cards">
              <Typography variant="h3" align="center">
                Compare crypto cards
              </Typography>
              <CryptoCardFilters regions={regions} onFilter={onFilterCards} />
              <CryptoCardGrid cards={selectedCryptoCards} />
            </div>

            <div>
              <FAQSection faqs={faqs} />
              <div className={classes.section}>
                <SeoText />
              </div>
              <LearnMore
                header="Learn more about crypto"
                title="Cards explained!"
                content="If you’re a crypto-trading beginner, look for a platform with a simple and straightforward interface. A good exchange starts simple, and hides complex features for advanced traders in the settings."
                cta={{
                  label: 'Learn more about Crypto Cards ->',
                  url: urls.cryptoCards,
                }}
              />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </FiltersProvider>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let cards: ICryptoCard[] = []
  let regions: Region[] = []

  try {
    cards = await getCryptoCards()
    regions = await getRegions()
  } catch (e) {
    console.error(e)
  }

  return {
    props: {
      cards,
      regions,
    },
  }
}

export default Cards
